/* Invoice panel extras: Missing invoices card (slip saved, no invoice) + bulk Print / Download bar. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Download, Loader2, Package, Printer, XCircle } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { loadMissing } from "@/components/MissingFilesCard";

export const ShippingCell = ({ r, onSaved }) => {
  const cur = r.shipping_usd ? String(r.shipping_usd) : "";
  const [v, setV] = useState(cur);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(cur); }, [cur]);
  const save = async () => {
    if (v.trim() === cur || (v.trim() === "" && !r.shipping_usd)) return setV(cur);
    setBusy(true);
    try {
      const res = await axios.put(`${API}/invoice-doc/history/${r.id}/shipping`, { shipping_usd: v.trim() || "0" }, { headers: getAuthHeader() });
      onSaved(res.data);
      toast.success(`Shipping $${Number(res.data.shipping_usd).toFixed(2)} saved — invoice, EDF & PB updated`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save shipping"); setV(cur); }
    setBusy(false);
  };
  return (
    <span className="inline-flex items-center gap-1">
      <Input value={v} onChange={(e) => setV(e.target.value)} onBlur={save} placeholder="$ 0.00" inputMode="decimal"
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} disabled={busy}
        className="h-7 w-20 text-right text-xs" data-testid={`inv-shipping-${r.invoice_no}`} />
      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-600" />}
    </span>
  );
};

export const MissingInvoicesCard = ({ refreshKey, onMade }) => {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => loadMissing("invoice").then(setList), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const make = async (inv) => {
    setBusy(inv);
    try {
      await axios.post(`${API}/invoice-doc/make/${encodeURIComponent(inv)}`, {}, { headers: getAuthHeader() });
      toast.success(`Invoice ${inv} created`); load(); onMade?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the invoice"); }
    setBusy("");
  };
  if (!list) return null;
  if (!list.length) return (
    <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700" data-testid="inv-missing-card">
      <CheckCircle2 className="h-4 w-4" /> Missing invoices <span className="rounded-full bg-emerald-600 px-1.5 text-[10px] text-white" data-testid="inv-missing-count">0</span>
      <span className="font-normal">— every completed invoice (today/yesterday) with a packing slip has its invoice</span>
    </div>);
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2" data-testid="inv-missing-card">
      <div className="flex items-center gap-2 text-xs font-semibold text-amber-800">
        <AlertTriangle className="h-4 w-4" /> Missing invoices
        <span className="rounded-full bg-amber-500 px-1.5 text-[10px] text-white" data-testid="inv-missing-count">{list.length}</span>
        <span className="font-normal">— completed today/yesterday, packing slip saved, no invoice yet</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {list.map(m => (
          <button key={m.invoice_key} type="button" disabled={!!busy} onClick={() => make(m.invoice_no)}
            className="inline-flex items-center gap-1 rounded border border-amber-300 bg-white px-2 py-0.5 font-mono text-[11px] font-semibold text-slate-700 hover:bg-amber-100 disabled:opacity-60"
            data-testid={`inv-missing-chip-${m.invoice_key}`}>
            {busy === m.invoice_no ? <Loader2 className="h-3 w-3 animate-spin" /> : null}{m.invoice_no}
            <span className="font-sans text-[9px] text-amber-600">Make</span>
          </button>))}
      </div>
    </div>
  );
};

// Send a PDF blob URL straight to the browser's print window (hard copy).
export const printPdfUrl = (url) => {
  const f = document.createElement("iframe");
  f.style.display = "none"; f.src = url;
  f.onload = () => { f.contentWindow?.focus(); f.contentWindow?.print(); };
  document.body.appendChild(f);
  setTimeout(() => { f.remove(); URL.revokeObjectURL(url); }, 120000);
};

const SHORT = { invoice: "Inv", edf: "EDF", pb: "PB", packing_list: "PL", bulk_data: "BD" };
const copiesText = (c) => Object.entries(c || {}).filter(([, n]) => n > 0).map(([k, n]) => `${SHORT[k] || k} ${n}`).join(", ");
const when = (iso) => new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

// Printed again? Ask first (every print is still logged).
export const confirmReprint = (lp, inv) => !lp || window.confirm(
  `${inv} was already printed by ${lp.by_name} on ${when(lp.at)} (${copiesText(lp.copies)}).\n\nPrint again?`);

// "Printed ✓" stamp — who, when, copies; tap for the full print history.
export const PrintedStamp = ({ r }) => {
  const [hist, setHist] = useState(null);
  const lp = r.last_print;
  if (!lp) return <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500" data-testid={`inv-printed-${r.invoice_no}`}>Not printed</span>;
  const toggle = async () => {
    if (hist) return setHist(null);
    try { setHist((await axios.get(`${API}/invoice-doc/prints/${encodeURIComponent(r.invoice_no)}`, { headers: getAuthHeader() })).data); }
    catch { toast.error("Could not load print history"); }
  };
  return (
    <span className="relative inline-block">
      <button type="button" onClick={toggle} title="Tap for print history" data-testid={`inv-printed-${r.invoice_no}`}
        className="rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-left text-[10px] leading-tight text-emerald-800 hover:bg-emerald-100">
        <span className="font-bold">Printed ✓{r.print_count > 1 ? ` ×${r.print_count}` : ""}</span> {lp.by_name} · {when(lp.at)}
        <span className="block text-emerald-700">{copiesText(lp.copies)}</span>
      </button>
      {hist && (
        <div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-lg border bg-white p-2 text-[11px] shadow-lg" data-testid={`inv-print-history-${r.invoice_no}`}>
          <p className="mb-1 font-semibold text-slate-700">Print history · {r.invoice_no}</p>
          {hist.map(h => (
            <div key={h.id} className="flex gap-2 border-t py-1 first:border-0">
              <span className="w-24 shrink-0 text-slate-500">{when(h.at)}</span>
              <span className="flex-1"><b>{h.by_name}</b> — {copiesText(h.copies)}</span>
            </div>))}
        </div>)}
    </span>
  );
};

const fetchBulk = async (ids, print = false) => {
  const r = await axios.post(`${API}/invoice-doc/bulk`, { ids, print }, { headers: getAuthHeader(), responseType: "blob" });
  return URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
};

export const BulkInvoiceBar = ({ selected, onClear, onPrinted }) => {
  const [busy, setBusy] = useState("");
  const ids = [...selected];
  if (!ids.length) return null;
  const run = async (kind) => {
    setBusy(kind);
    try {
      const url = await fetchBulk(ids, kind === "print");
      if (kind === "print") { printPdfUrl(url); onPrinted?.(); }
      else {
        const a = document.createElement("a");
        a.href = url; a.download = `EXPORT INVOICES (${ids.length}).pdf`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      }
    } catch (e) { toast.error("Could not build the combined PDF"); }
    setBusy("");
  };
  return (
    <div className="mb-3 flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm" data-testid="inv-bulk-bar">
      <span className="font-semibold text-indigo-800" data-testid="inv-bulk-count">{ids.length} selected</span>
      <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" disabled={!!busy} onClick={() => run("print")} data-testid="inv-bulk-print">
        {busy === "print" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Printer className="mr-1 h-4 w-4" />} Print
      </Button>
      <Button size="sm" variant="outline" className="h-8" disabled={!!busy} onClick={() => run("dl")} data-testid="inv-bulk-download">
        {busy === "dl" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Download one PDF
      </Button>
      <button type="button" className="ml-auto text-xs text-slate-500 hover:underline" onClick={onClear} data-testid="inv-bulk-clear">Clear</button>
    </div>
  );
};

const FMTS = [["pdf", "One merged PDF"], ["zip", "ZIP (PDF + Excel)"]];

const COPY_KEY = "pack-print-copies";
const DEF_COPIES = { invoice: 2, edf: 1, pb: 3, packing_list: 2, bulk_data: 0 };
const loadCopies = () => { try { return { ...DEF_COPIES, ...JSON.parse(localStorage.getItem(COPY_KEY) || "{}") }; } catch { return { ...DEF_COPIES }; } };

// Print the export pack straight away with the saved copy numbers (only documents that are ready).
export const printPackNow = async (invoiceNo) => {
  const st = (await axios.get(`${API}/invoice-doc/pack-status/${encodeURIComponent(invoiceNo)}`, { headers: getAuthHeader() })).data;
  const c = loadCopies();
  if (!st.items.find(i => i.key === "invoice")?.ready) throw new Error(`${invoiceNo}: Tax Invoice not made yet — make it first, then print the pack`);
  const copies = st.items.filter(i => i.ready && c[i.key] > 0).map(i => `${i.key}:${c[i.key]}`).join(",");
  if (!copies) throw new Error("No ready document has copies set");
  const r = await axios.get(`${API}/invoice-doc/pack-print/${encodeURIComponent(invoiceNo)}`, { headers: getAuthHeader(), responseType: "blob", params: { copies } });
  printPdfUrl(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })));
  return st.items.filter(i => !i.ready).map(i => i.label);
};

// Hard copies for the printer — remembered per browser; Bulk Data 0 by default.
const PrintCopies = ({ st, invoiceNo, busy, onPrint }) => {
  const [c, setC] = useState(loadCopies);
  const set = (k, d) => setC(o => { const n = { ...o, [k]: Math.max(0, Math.min(20, (o[k] || 0) + d)) }; localStorage.setItem(COPY_KEY, JSON.stringify(n)); return n; });
  const ready = st.items.filter(i => i.ready);
  const total = ready.reduce((a, i) => a + (c[i.key] || 0), 0);
  return (
    <div className="mt-3 border-t pt-2" data-testid={`inv-print-copies-${invoiceNo}`}>
      <p className="mb-1 font-semibold text-slate-700">Hard copy print · copies</p>
      {st.items.map(i => (
        <div key={i.key} className={`flex items-center gap-2 py-0.5 ${i.ready ? "" : "opacity-50"}`}>
          <span className="flex-1">{i.label}</span>
          <button type="button" disabled={!i.ready || !c[i.key]} onClick={() => set(i.key, -1)} className="h-6 w-6 rounded border text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40" data-testid={`inv-copies-minus-${i.key}`}>−</button>
          <span className="w-5 text-center font-mono font-semibold" data-testid={`inv-copies-${i.key}`}>{i.ready ? c[i.key] || 0 : 0}</span>
          <button type="button" disabled={!i.ready} onClick={() => set(i.key, 1)} className="h-6 w-6 rounded border text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40" data-testid={`inv-copies-plus-${i.key}`}>+</button>
        </div>))}
      <Button size="sm" variant="outline" className="mt-2 h-9 w-full border-emerald-600 text-xs font-semibold text-emerald-700 hover:bg-emerald-50" disabled={!total || busy}
        onClick={() => onPrint(Object.fromEntries(ready.map(i => [i.key, c[i.key] || 0])))} data-testid={`inv-pack-print-${invoiceNo}`}>
        <Printer className="mr-1 h-4 w-4" /> {total ? `Print ${total} cop${total > 1 ? "ies" : "y"}` : "Set at least one copy"}</Button>
    </div>
  );
};

const PackPicker = ({ st, invoiceNo, busy, big, onDownload, onPrint }) => {
  const [pick, setPick] = useState(() => new Set(st.items.filter(i => i.ready).map(i => i.key)));
  const [fmt, setFmt] = useState("pdf");
  const flip = (k) => setPick(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  return (
    <div className={`${big ? "relative mt-2 w-full" : "absolute right-0 top-full z-30 mt-1 w-72"} rounded-lg border bg-white p-3 text-left text-xs shadow-lg`} data-testid={`inv-pack-pop-${invoiceNo}`}>
      <p className="mb-1 font-semibold text-slate-700">Export pack · {invoiceNo}</p>
      {st.items.map(i => (
        <label key={i.key} className={`flex items-start gap-2 py-1 ${i.ready ? "cursor-pointer" : "opacity-60"}`} data-testid={`inv-pack-item-${i.key}`}>
          <input type="checkbox" className="mt-0.5 h-4 w-4" disabled={!i.ready} checked={pick.has(i.key)} onChange={() => flip(i.key)} data-testid={`inv-pack-tick-${i.key}`} />
          {i.ready ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="mt-0.5 h-3.5 w-3.5 text-rose-500" />}
          <span>{i.label}{!i.ready && <span className="block text-[10px] text-rose-600">{i.reason}</span>}</span>
        </label>))}
      <div className="mt-2 flex gap-1">
        {FMTS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFmt(k)} data-testid={`inv-pack-fmt-${k}`}
            className={`flex-1 rounded border px-1.5 py-1.5 text-[11px] font-semibold transition-colors ${fmt === k ? "border-indigo-600 bg-indigo-50 text-indigo-700" : "text-slate-600"}`}>{l}</button>))}
      </div>
      <Button size="sm" className="mt-2 h-9 w-full bg-indigo-600 text-xs hover:bg-indigo-700" disabled={!pick.size || busy}
        onClick={() => onDownload([...pick], fmt)} data-testid={`inv-pack-download-${invoiceNo}`}>
        {pick.size ? `Download ${pick.size} doc${pick.size > 1 ? "s" : ""} · ${fmt.toUpperCase()}` : "Tick at least one"}</Button>
      <PrintCopies st={st} invoiceNo={invoiceNo} busy={busy} onPrint={onPrint} />
    </div>
  );
};

export const ExportPackButton = ({ invoiceNo, big, lastPrint, onPrinted }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async () => {
    setBusy(true);
    try { setSt((await axios.get(`${API}/invoice-doc/pack-status/${encodeURIComponent(invoiceNo)}`, { headers: getAuthHeader() })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not check documents"); }
    setBusy(false);
  };
  const download = async (parts, fmt) => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/invoice-doc/pack/${encodeURIComponent(invoiceNo)}`,
        { headers: getAuthHeader(), responseType: "blob", params: { parts: parts.join(","), fmt } });
      const url = URL.createObjectURL(new Blob([r.data], { type: fmt === "pdf" ? "application/pdf" : "application/zip" }));
      const a = document.createElement("a"); a.href = url; a.download = `EXPORT PACK ${invoiceNo}.${fmt}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000); setSt(null);
    } catch (e) {
      let msg = "Export pack not ready";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* keep default */ }
      toast.error(msg);
    }
    setBusy(false);
  };
  const print = async (copies) => {
    if (!confirmReprint(lastPrint, invoiceNo)) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/invoice-doc/pack-print/${encodeURIComponent(invoiceNo)}`, { headers: getAuthHeader(), responseType: "blob",
        params: { copies: Object.entries(copies).map(([k, n]) => `${k}:${n}`).join(",") } });
      printPdfUrl(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }))); setSt(null); onPrinted?.();
    } catch (e) {
      let msg = "Could not prepare the print";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* keep default */ }
      toast.error(msg);
    }
    setBusy(false);
  };
  const toggle = () => (st ? setSt(null) : check());
  return (
    <span className={`relative ${big ? "block w-full" : "inline-block"}`}>
      {big ? (
        <Button className="h-11 w-full bg-indigo-600 text-sm font-semibold hover:bg-indigo-700" disabled={busy} onClick={toggle} data-testid={`inv-pack-m-${invoiceNo}`}>
          {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Package className="mr-2 h-5 w-5" />} Export pack</Button>
      ) : (
        <Button size="sm" variant="ghost" className="h-7 px-1.5 text-[11px] font-semibold text-indigo-700" disabled={busy} onClick={toggle}
          title="Export pack (Invoice + EDF + PB + Packing List + Bulk Data)" data-testid={`inv-pack-${invoiceNo}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}</Button>)}
      {st && <PackPicker st={st} invoiceNo={invoiceNo} busy={busy} big={big} onDownload={download} onPrint={print} />}
    </span>
  );
};
